import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ReviewCategory {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  keywords: string[] | null;
  usp: string | null;
  location: string | null;
  created_at: string;
}

export function useCategories(businessId: string | undefined) {
  const [categories, setCategories] = useState<ReviewCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (businessId) {
      fetchCategories();
    } else {
      setLoading(false);
    }
  }, [businessId]);

  const fetchCategories = async () => {
    if (!businessId) return;

    try {
      const { data, error } = await supabase
        .from('review_categories')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async (
    name: string, 
    description?: string,
    keywords?: string[],
    usp?: string,
    location?: string
  ) => {
    if (!businessId) return { error: new Error('No business found') };

    try {
      const { data, error } = await supabase
        .from('review_categories')
        .insert({
          business_id: businessId,
          name,
          description: description || null,
          keywords: keywords || [],
          usp: usp || null,
          location: location || null,
        })
        .select()
        .single();

      if (error) throw error;
      
      setCategories([...categories, data]);
      toast.success('Category created!');
      return { data };
    } catch (err) {
      const error = err as Error;
      toast.error(error.message);
      return { error };
    }
  };

  const updateCategory = async (
    categoryId: string,
    updates: { keywords?: string[]; usp?: string; location?: string }
  ) => {
    try {
      const { data, error } = await supabase
        .from('review_categories')
        .update(updates)
        .eq('id', categoryId)
        .select()
        .single();

      if (error) throw error;
      
      setCategories(categories.map(c => c.id === categoryId ? data : c));
      toast.success('Category updated!');
      return { data };
    } catch (err) {
      const error = err as Error;
      toast.error(error.message);
      return { error };
    }
  };

  const deleteCategory = async (categoryId: string) => {
    try {
      const { error } = await supabase
        .from('review_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;
      
      setCategories(categories.filter(c => c.id !== categoryId));
      toast.success('Category deleted!');
      return { success: true };
    } catch (err) {
      const error = err as Error;
      toast.error(error.message);
      return { error };
    }
  };

  return {
    categories,
    loading,
    createCategory,
    updateCategory,
    deleteCategory,
    refetch: fetchCategories,
  };
}
